import { useEffect, useMemo, useRef, useState } from "react"
import { useNavigate, useParams, useLocation } from "react-router-dom"
import { motion } from "framer-motion"
import { AlertCircle, Loader2, MailCheck } from "lucide-react"
import { confirmRegister } from "@/api/auth"

export default function SignupVerifyPage() {
  const nav = useNavigate()
  const { token: tokenParam } = useParams()
  const location = useLocation()

  const tokenFromQuery = useMemo(() => {
    const q = new URLSearchParams(location.search)
    return q.get("token") || ""
  }, [location.search])

  const token = useMemo(() => {
    const raw = tokenParam || tokenFromQuery || ""
    try { return decodeURIComponent(raw) } catch { return raw }
  }, [tokenParam, tokenFromQuery])

  const [state, setState] = useState({ phase: "idle", msg: "" })

  const effectRan = useRef(false)
  const navOnce = useRef(false)
  const navLogin = () => {
    if (navOnce.current) return
    navOnce.current = true
    nav("/login", { replace: true })
  }

  useEffect(() => {
    if (effectRan.current) return
    effectRan.current = true

    if (!token) {
      setState({ phase: "error", msg: "ลิงก์ไม่ถูกต้องหรือหมดอายุ" })
      return
    }

    const ac = new AbortController()
    ;(async () => {
      setState({ phase: "loading", msg: "" })
      try {
        await confirmRegister(token, { signal: ac.signal })
        navLogin()
        return
      } catch (err) {
        const code = err?.response?.data?.detail || err?.response?.data?.error || ""
        if (code === "duplicate_user") { navLogin(); return }
        const msg = code === "invalid_token"
          ? "ลิงก์ไม่ถูกต้องหรือหมดอายุ"
          : "ไม่สามารถยืนยันการสมัครได้ในขณะนี้"
        setState({ phase: "error", msg })
      }
    })()

    return () => ac.abort()
  }, [token, nav])

  return (
    <div className="min-h-screen w-full bg-gradient-to-b from-slate-900 via-slate-900 to-slate-800 flex items-center justify-center p-4">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
        <div className="bg-slate-950/60 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/10 p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 rounded-xl bg-white/5">
              <MailCheck className="w-6 h-6 text-white/90" />
            </div>
            <h1 className="text-xl font-semibold text-white">กำลังยืนยันอีเมลของคุณ</h1>
          </div>

          {state.phase === "loading" && (
            <div className="flex flex-col items-center text-center gap-4 py-6">
              <Loader2 className="w-10 h-10 animate-spin text-white/80" />
              <p className="text-white/80">โปรดรอสักครู่…</p>
            </div>
          )}

          {state.phase === "error" && (
            <div className="flex flex-col items-center text-center gap-4 py-6">
              <AlertCircle className="w-12 h-12 text-rose-400" />
              <p className="text-white">{state.msg}</p>
              <div className="flex gap-3 w-full">
                <button
                  onClick={() => window.location.reload()}
                  className="w-1/2 inline-flex items-center justify-center rounded-xl px-4 py-3 bg-white/10 text-white hover:bg-white/15 active:scale-[.99] transition"
                >
                  ลองใหม่
                </button>
                <button
                  onClick={() => navLogin()}
                  className="w-1/2 inline-flex items-center justify-center rounded-xl px-4 py-3 bg-white text-slate-900 font-medium hover:bg-white/90 active:scale-[.99] transition"
                >
                  ไปหน้าเข้าสู่ระบบ
                </button>
              </div>
            </div>
          )}

          <div className="mt-6 text-center text-xs text-white/50">
            หากกดลิงก์ไม่ติด ให้คัดลอกลิงก์ทั้งหมดแล้ววางในแถบที่อยู่ของเบราว์เซอร์
          </div>
        </div>
      </motion.div>
    </div>
  )
}
